// src/pages/ActualitesPage.tsx
import React, { useState } from 'react';
import { usePosts, useCategories, usePostSearch } from '../hooks/useWordpress';
import { NewsCard } from '../components/News/NewsCard';

const ActualitesPage: React.FC = () => {
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [showSearch, setShowSearch] = useState(false);

    // Hook pour les posts avec filtres
    const {
        posts,
        loading,
        error,
        loadMore,
        hasMore,
        currentPage,
        totalPages
    } = usePosts({
        per_page: 12,
        categories: selectedCategory,
        orderby: 'date',
        order: 'desc'
    });

    // Hook pour les catégories
    const { categories } = useCategories();

    // Hook pour la recherche
    const {
        query,
        setQuery,
        posts: searchPosts,
        loading: searchLoading,
        hasQuery
    } = usePostSearch();

    const displayPosts = hasQuery ? searchPosts : posts;
    const isLoading = hasQuery ? searchLoading : loading;

    return (
        <div className="min-h-screen bg-gray-50">
            <br/><br/><br/>
            {/* Hero Section */}
            <div className="bg-blue-900 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h1 className="text-4xl md:text-5xl font-bold mb-4">
                            Actualités UDB
                        </h1>
                        <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
                            Suivez les dernières nouvelles et événements de l'Union Démocratique des Bâtisseurs
                        </p>
                    </div>
                </div>
            </div>

            {/* Filtres et recherche */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        {/* Filtres par catégorie */}
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium text-gray-700">Catégories:</span>
                            <button
                                onClick={() => setSelectedCategory('')}
                                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                    selectedCategory === ''
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                Toutes
                            </button>
                            {categories.map((category) => (
                                <button
                                    key={category.id}
                                    onClick={() => setSelectedCategory(category.id.toString())}
                                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                        selectedCategory === category.id.toString()
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    {category.name} ({category.count})
                                </button>
                            ))}
                        </div>

                        {/* Barre de recherche */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setShowSearch(!showSearch)}
                                className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                                </svg>
                            </button>
                            {showSearch && (
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Rechercher un article..."
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        className="w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                    {query && (
                                        <button
                                            onClick={() => setQuery('')}
                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor"
                                                 viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M6 18L18 6M6 6l12 12"/>
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Contenu principal */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Indicateur de recherche */}
                {hasQuery && (
                    <div className="mb-8 p-4 bg-blue-50 rounded-lg">
                        <p className="text-blue-800">
                            <span className="font-medium">Recherche :</span> "{query}"
                            <span className="text-blue-600 ml-2">
                ({searchPosts.length} résultat{searchPosts.length !== 1 ? 's' : ''})
              </span>
                        </p>
                    </div>
                )}

                {/* Gestion des erreurs */}
                {error && (
                    <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center">
                            <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor"
                                 viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L5.082 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                            </svg>
                            <p className="text-red-800">{error}</p>
                        </div>
                    </div>
                )}

                {/* Loading state */}
                {isLoading && displayPosts.length === 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
                                <div className="h-56 bg-gray-200"></div>
                                <div className="p-6">
                                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                                    <div className="h-6 bg-gray-200 rounded mb-3"></div>
                                    <div className="space-y-2">
                                        <div className="h-4 bg-gray-200 rounded"></div>
                                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Articles */}
                {displayPosts.length > 0 && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {displayPosts.map((post) => (
                                <NewsCard key={post.id} post={post}/>
                            ))}
                        </div>

                        {/* Pagination / Load More */}
                        {!hasQuery && hasMore && (
                            <div className="text-center mt-12">
                                <button
                                    onClick={loadMore}
                                    disabled={loading}
                                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {loading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none"
                                                 viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10"
                                                        stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor"
                                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Chargement...
                                        </>
                                    ) : (
                                        <>
                                            Charger plus d'articles
                                            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor"
                                                 viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M19 9l-7 7-7-7"/>
                                            </svg>
                                        </>
                                    )}
                                </button>
                                <p className="text-sm text-gray-500 mt-2">
                                    Page {currentPage} sur {totalPages}
                                </p>
                            </div>
                        )}
                    </>
                )}

                {/* État vide */}
                {!isLoading && displayPosts.length === 0 && !error && (
                    <div className="text-center py-12">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor"
                             viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun article trouvé</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {hasQuery
                                ? `Aucun article ne correspond à votre recherche "${query}".`
                                : 'Aucun article n\'est disponible pour le moment.'
                            }
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ActualitesPage;
